import React, { useState } from 'react';
import {
  StepContainer,
  SectionHeader,
  SectionTitle,
  SectionDivider,
  SectionDescription,
  ButtonContainer,
  NextButton,
  SecondaryButton,
  InputRow,
} from '../../styles';
import MobileInput from '../common/MobileInput';
import MobileSelect from '../common/MobileSelect';
import { MobileAnalysisFormData } from '../../types';
import { propertyTypeOptions, getReasonOptions, ufOptions } from '../../constants';
import useAddressLookup from '../../hooks/useAddressLookup';

interface Step3Props {
  formData: MobileAnalysisFormData;
  updateFormData: (data: Partial<MobileAnalysisFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  userId?: string;
}

const Step3_PropertyAddress: React.FC<Step3Props> = ({
  formData,
  updateFormData,
  onNext,
  onBack,
  userId,
}) => {
  const { lookup, isLoading, error: lookupError } = useAddressLookup();
  const [autoFilledFields, setAutoFilledFields] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const reasonOptions = getReasonOptions(formData.tenantType, formData.purpose);

  // Handle CEP lookup
  const handleZipCodeChange = async (zipCode: string) => {
    updateFormData({
      property: { ...formData.property, zipCode },
    });

    // Lookup when CEP is complete (9 chars with mask: 00000-000)
    if (zipCode.replace(/\D/g, '').length === 8) {
      const result = await lookup(zipCode, userId);
      if (result) {
        const filledFields: string[] = [];

        const updates = { ...formData.property, zipCode };

        if (result.street) {
          updates.street = result.street;
          filledFields.push('street');
        }
        if (result.district) {
          updates.district = result.district;
          filledFields.push('district');
        }
        if (result.city) {
          updates.city = result.city;
          filledFields.push('city');
        }
        if (result.state) {
          updates.state = result.state;
          filledFields.push('state');
        }

        updateFormData({ property: updates });
        setAutoFilledFields(filledFields);
      }
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    const property = formData.property;

    if (!property.zipCode || property.zipCode.replace(/\D/g, '').length !== 8) {
      newErrors.zipCode = 'CEP inválido';
    }
    if (!property.street?.trim()) {
      newErrors.street = 'Logradouro é obrigatório';
    }
    if (!property.number?.trim()) {
      newErrors.number = 'Número é obrigatório';
    }
    if (!property.district?.trim()) {
      newErrors.district = 'Bairro é obrigatório';
    }
    if (!property.city?.trim()) {
      newErrors.city = 'Cidade é obrigatória';
    }
    if (!property.state?.trim()) {
      newErrors.state = 'UF é obrigatório';
    }
    if (!property.propertyType) {
      newErrors.propertyType = 'Tipo de imóvel é obrigatório';
    }
    if (!property.reason) {
      newErrors.reason = 'Motivo da locação é obrigatório';
    }

    // Complement is required for apartments
    if (property.propertyType === 'APARTAMENTO' && !property.complement?.trim()) {
      newErrors.complement = 'Complemento é obrigatório para apartamentos';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      onNext();
    }
  };

  const isApartment = formData.property.propertyType === 'APARTAMENTO';

  return (
    <StepContainer>
      <SectionHeader>
        <SectionTitle>Endereço do Imóvel</SectionTitle>
        <SectionDivider />
      </SectionHeader>

      <SectionDescription>
        Informe o CEP para preencher o endereço automaticamente.
      </SectionDescription>

      {/* CEP */}
      <MobileInput
        label="CEP"
        value={formData.property.zipCode}
        onChange={handleZipCodeChange}
        placeholder="00000-000"
        mask="99999-999"
        required
        error={errors.zipCode || lookupError || undefined}
        loading={isLoading}
        inputMode="numeric"
      />

      {/* Street */}
      <MobileInput
        label="Logradouro"
        value={formData.property.street}
        onChange={(value) =>
          updateFormData({
            property: { ...formData.property, street: value },
          })
        }
        placeholder="Rua, Avenida, etc."
        required
        error={errors.street}
        autoFilled={autoFilledFields.includes('street')}
      />

      {/* Number and Complement */}
      <InputRow>
        <MobileInput
          label="Número"
          value={formData.property.number}
          onChange={(value) =>
            updateFormData({
              property: { ...formData.property, number: value },
            })
          }
          placeholder="Nº"
          required
          error={errors.number}
          inputMode="numeric"
        />

        <MobileInput
          label="Complemento"
          value={formData.property.complement}
          onChange={(value) =>
            updateFormData({
              property: { ...formData.property, complement: value },
            })
          }
          placeholder="Apto, Bloco, etc."
          required={isApartment}
          error={errors.complement}
        />
      </InputRow>

      {/* District */}
      <MobileInput
        label="Bairro"
        value={formData.property.district}
        onChange={(value) =>
          updateFormData({
            property: { ...formData.property, district: value },
          })
        }
        placeholder="Bairro"
        required
        error={errors.district}
        autoFilled={autoFilledFields.includes('district')}
      />

      {/* City and State */}
      <InputRow>
        <MobileInput
          label="Cidade"
          value={formData.property.city}
          onChange={(value) =>
            updateFormData({
              property: { ...formData.property, city: value },
            })
          }
          placeholder="Cidade"
          required
          error={errors.city}
          autoFilled={autoFilledFields.includes('city')}
          disabled
        />

        <MobileSelect
          label="UF"
          value={formData.property.state}
          onChange={(value) =>
            updateFormData({
              property: { ...formData.property, state: value },
            })
          }
          options={ufOptions}
          required
          error={errors.state}
          disabled={autoFilledFields.includes('state')}
        />
      </InputRow>

      {/* Property Type */}
      <MobileSelect
        label="Tipo do Imóvel"
        value={formData.property.propertyType}
        onChange={(value) =>
          updateFormData({
            property: { ...formData.property, propertyType: value },
          })
        }
        options={propertyTypeOptions}
        required
        error={errors.propertyType}
      />

      {/* Reason */}
      <MobileSelect
        label="Motivo da Locação"
        value={formData.property.reason}
        onChange={(value) =>
          updateFormData({
            property: { ...formData.property, reason: value },
          })
        }
        options={reasonOptions}
        required
        error={errors.reason}
      />

      <ButtonContainer>
        <SecondaryButton type="button" onClick={onBack}>
          Voltar
        </SecondaryButton>
        <NextButton type="button" onClick={handleNext}>
          Próximo
        </NextButton>
      </ButtonContainer>
    </StepContainer>
  );
};

export default Step3_PropertyAddress;
